import { z } from "zod";

/**
 * MerchantSpec — the single contract for the dev merchant factory.
 * Built by the admin form, stored by presets, validated by the
 * createDevMerchant mutation, executed by the dev-merchant-create job runner.
 * Every field is concrete: the backend never invents data.
 */

export const addressSchema = z.object({
  line1: z.string().min(1),
  city: z.string().min(1),
  state: z.string().min(2).max(3),
  postalCode: z.string().min(3),
  country: z.enum(["CA", "US"]),
});

export const identitySchema = z.object({
  email: z.string().email(),
  /** Cognito login password. Plaintext by design: synthetic dev accounts only. */
  password: z.string().min(8).default("Pass123$"),
  givenName: z.string().min(1),
  familyName: z.string().min(1),
  phone: z.string().regex(/^\+1\d{10}$/, "E.164 NA number, e.g. +14165551234"),
  dob: z.object({
    day: z.number().int().min(1).max(31),
    month: z.number().int().min(1).max(12),
    year: z.number().int().min(1900).max(2010),
  }),
  address: addressSchema,
  timezone: z.string().default("America/Toronto"),
  cognitoGroups: z.array(z.string()).default([]),
});

/**
 * UserProfile.settings knobs. Optional throughout — omitted fields get the
 * "already educated, TTP enabled" defaults so factory merchants skip the v1
 * education gate unless an edge case wants otherwise.
 */
export const userSettingsSchema = z.object({
  tapToPay: z
    .object({
      previouslyEnabled: z.boolean().default(true),
      /** Education version already seen; 0 = never shown (mobile's current version is 1). */
      educationShown: z.number().int().min(0).max(10).default(1),
    })
    .prefault({}),
});

export const businessSchema = z.object({
  name: z.string().min(1).max(80),
  /** Microsite slug base; derived from name when omitted. */
  slug: z
    .string()
    .regex(/^[A-Za-z0-9]+$/)
    .optional(),
  type: z.enum(["individual", "company"]).default("individual"),
  country: z.enum(["CA", "US"]).default("CA"),
  mcc: z
    .string()
    .regex(/^\d{4}$/)
    .default("5734"),
  productDescription: z
    .string()
    .default("Point-of-sale payment processing (dev factory)"),
  /**
   * Stripe business_profile.url; a custom account without one carries an
   * outstanding requirement. Falls back to the seed URL when omitted.
   */
  url: z.string().url().optional(),
  onboardingStatus: z.enum(["completed", "in_progress", "incomplete"]),
  subscriptionStatus: z.enum(["active", "trialing", "past_due", "inactive"]),
  identityVerificationStatus: z.enum(["verified", "pending"]).optional(),
  payoutInterval: z.enum(["manual", "daily", "weekly", "monthly"]).optional(),
  transactionLimitCents: z.number().int().positive().optional(),
  /** Escape hatch merged last into BusinessProfile.create params. */
  profileParams: z.record(z.string(), z.unknown()).default({}),
});

export const stripeConfigSchema = z.object({
  createConnectedAccount: z.boolean().default(true),
  subscription: z
    .object({
      skip: z.boolean().default(false),
      trialDays: z.number().int().min(1).max(365).optional(),
    })
    .prefault({}),
  /**
   * When true, omit the verification documents, id number, and external
   * account so Stripe leaves charges disabled — reproduces a restricted
   * merchant.
   */
  restricted: z.boolean().default(false),
});

export const transactionDescriptorSchema = z.object({
  targetStatus: z.enum(["Succeeded", "Refunded", "Failed", "Voided"]),
  amount: z.number().int().min(50).max(1_000_000),
  currency: z.enum(["cad", "usd"]),
  createdDaysAgo: z.number().int().min(0).max(365),
  entryMode: z
    .enum(["chip", "contactless", "swipe", "manual", "fallback"])
    .default("contactless"),
  cardLast4: z
    .string()
    .regex(/^\d{4}$/)
    .default("4242"),
  refundedDaysAgo: z.number().int().min(0).optional(),
  refundedAmount: z.number().int().positive().optional(),
  refundNote: z.string().optional(),
});

export const invoiceDescriptorSchema = z.object({
  amount: z.number().int().min(50).max(1_000_000),
  currency: z.enum(["cad", "usd"]),
  status: z.enum(["Draft", "Sent", "Paid"]),
  description: z.string().min(1),
  recipientName: z.string().min(1),
  /** email address or E.164 phone, matching deliveryMethod. */
  recipientContact: z.string().min(3),
  deliveryMethod: z.enum(["email", "sms"]).default("email"),
  createdDaysAgo: z.number().int().min(0).max(365),
  /** Days from now until due; negative = already past due. */
  dueInDays: z.number().int().min(-365).max(365).default(14),
  paidDaysAgo: z.number().int().min(0).optional(),
});

export const payoutDescriptorSchema = z.object({
  amount: z.number().int().min(100).max(10_000_000),
  currency: z.enum(["cad", "usd"]),
  status: z.enum(["paid", "in_transit"]),
  createdDaysAgo: z.number().int().min(0).max(365),
  arrivalInDays: z.number().int().min(-365).max(30).default(2),
});

export const staffDescriptorSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  isOwner: z.boolean().default(false),
  isActive: z.boolean().default(true),
  /** 4-digit PIN; hashed by the engine with hashStaffPin. */
  pin: z
    .string()
    .regex(/^\d{4}$/)
    .default("1234"),
});

export const entitiesSchema = z.object({
  transactions: z.array(transactionDescriptorSchema).max(100).default([]),
  invoices: z.array(invoiceDescriptorSchema).max(50).default([]),
  payouts: z.array(payoutDescriptorSchema).max(50).default([]),
  staff: z.array(staffDescriptorSchema).max(20).default([]),
  /** Create email/sms receipts for the first N created transactions. */
  receiptsForFirstTransactions: z.number().int().min(0).max(20).default(0),
});

export const lifecycleOverridesSchema = z.object({
  /** trackName -> stage, applied after the standard recompute. */
  overrides: z.record(z.string(), z.string()).default({}),
});

export const merchantSpecSchema = z.object({
  identity: identitySchema,
  business: businessSchema,
  stripe: stripeConfigSchema.prefault({}),
  entities: entitiesSchema.prefault({}),
  userSettings: userSettingsSchema.optional(),
  lifecycle: lifecycleOverridesSchema.optional(),
});

export type MerchantAddress = z.infer<typeof addressSchema>;
export type MerchantIdentity = z.infer<typeof identitySchema>;
export type MerchantBusiness = z.infer<typeof businessSchema>;
export type MerchantUserSettings = z.infer<typeof userSettingsSchema>;
export type MerchantStripeConfig = z.infer<typeof stripeConfigSchema>;
export type TransactionDescriptor = z.infer<typeof transactionDescriptorSchema>;
export type InvoiceDescriptor = z.infer<typeof invoiceDescriptorSchema>;
export type PayoutDescriptor = z.infer<typeof payoutDescriptorSchema>;
export type StaffDescriptor = z.infer<typeof staffDescriptorSchema>;
export type MerchantEntities = z.infer<typeof entitiesSchema>;
export type MerchantLifecycleOverrides = z.infer<
  typeof lifecycleOverridesSchema
>;
export type MerchantSpec = z.infer<typeof merchantSpecSchema>;

/** Result of one merchant's build, stored in Job.result.merchants[]. */
export interface DevMerchantResultEntry {
  index: number;
  status: "created" | "failed" | "building";
  email?: string;
  password?: string;
  businessId?: string;
  userId?: string;
  stripeConnectedAccountId?: string;
  failedPhase?: string;
  error?: string;
}

export const DEV_MERCHANT_JOB_TYPE = "dev-merchant-create";
